// src/config/googleAuth.ts
// Direct Google Identity Services (GIS) integration — no Firebase

declare global {
  interface Window {
    google: any;
  }
}

export interface GoogleAuthResult {
  success: boolean;
  googleIdToken?: string;
  googleUserInfo?: { name?: string; picture?: string; email?: string } | null;
  error?: string;
}

type AuthCallback = (result: GoogleAuthResult) => void;

let gisInitialized = false;
let activeCallback: AuthCallback | null = null;

// ─── Script loader ────────────────────────────────────────────────────────────

const loadGISScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) { resolve(); return; }

    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      const poll = setInterval(() => {
        if (window.google?.accounts?.id) { clearInterval(poll); resolve(); }
      }, 50);
      setTimeout(() => { clearInterval(poll); reject(new Error('GIS script load timeout')); }, 10000);
      return;
    }

    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });

// ─── Credential callback ──────────────────────────────────────────────────────

// Called by GIS when the user completes sign-in.
// GIS Sign In With Google id_tokens contain name / picture / email in the JWT payload.
const handleCredential = (response: { credential: string }) => {
  if (!activeCallback) return;
  const cb = activeCallback; // keep set so subsequent sign-ins still work

  try {
    const b64 = response.credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));

    cb({
      success: true,
      googleIdToken: response.credential,
      googleUserInfo: {
        name: payload.given_name || payload.name,
        picture: payload.picture,
        email: payload.email,
      },
    });
  } catch {
    cb({ success: false, error: 'Failed to process Google credential' });
  }
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Load the GIS script and initialise google.accounts.id.
 * Safe to call multiple times — only runs setup once.
 */
export const initGoogleAuth = async (): Promise<void> => {
  await loadGISScript();
  if (gisInitialized) return;

  window.google.accounts.id.initialize({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
    callback: handleCredential,
    cancel_on_tap_outside: true,
    auto_select: false,
    itp_support: true,
  });

  gisInitialized = true;
};

/**
 * Render the official Google Sign-In button into `container` and register
 * `callback` to receive the result when the user completes sign-in.
 *
 * Place `container` directly in the visible UI — GIS only renders into
 * on-viewport elements.  The rendered button opens the Google sign-in popup
 * when clicked by the user.
 */
export const renderGoogleButton = (
  container: HTMLElement,
  callback: AuthCallback,
  buttonText: 'signin_with' | 'signup_with' | 'continue_with' | 'signin' = 'signin_with',
): void => {
  activeCallback = callback;

  // clientWidth is 0 on first paint when element has no intrinsic size yet.
  // Fall back to the parent's width, then a safe default.
  const width =
    container.clientWidth ||
    container.parentElement?.clientWidth ||
    400;

  window.google.accounts.id.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: buttonText,
    shape: 'rectangular',
    logo_alignment: 'center',
    width: Math.max(width, 200),
  });
};

/**
 * Replace the active sign-in callback (e.g. on component unmount/cleanup).
 */
export const setGoogleAuthCallback = (callback: AuthCallback | null): void => {
  activeCallback = callback;
};

/**
 * GIS does not use Firebase-style redirect auth.
 * Always returns null so any existing callers degrade gracefully.
 */
export const checkRedirectResult = async (): Promise<GoogleAuthResult | null> => null;

/**
 * Disable GIS auto-select so the user is not silently re-signed-in after logout.
 */
export const signOutGoogle = async (): Promise<void> => {
  try { window.google?.accounts?.id?.disableAutoSelect(); } catch { /* noop */ }
};
